// Pure block-data helpers shared between the client editor preview
// and the worker email renderer. Kept in /shared because the worker
// can't import client modules (separate tsconfigs + no React deps in
// the worker).

import type {
  CalendarEventItem,
  CoverageHeadline,
  EventListItem,
  StoryBodyItem,
} from './types';
import { sortEvents } from './event-date';

// Returns the canonical story body sequence.
//
// Precedence is **paragraphs > body** when both are present. That
// asymmetry is deliberate: `paragraphs` is the legacy field and the
// editor's setBody clears it when the user commits a real edit, so a
// row with both fields populated is the unmistakable signature that
// `normalizeBlocks` merged in default `body` content over a legacy
// `paragraphs` row that the user never touched. Trusting `body` in
// that case is what corrupted real content during the v1 deploy of
// inline images. See CLAUDE.md ("Story body migration").
//
// IDs from the legacy path are deterministic (index-based) so React
// keys stay stable across renders until the user makes their first
// edit and the array gets persisted with real ids.
export function storyBodyItems(data: { body?: unknown; paragraphs?: unknown }): StoryBodyItem[] {
  if (Array.isArray(data.paragraphs) && data.paragraphs.length > 0) {
    return (data.paragraphs as Array<{ lead?: string; body?: string }>).map((p, i) => ({
      id: `legacy-p-${i}`,
      type: 'paragraph' as const,
      lead: p.lead || '',
      body: p.body || '',
    }));
  }
  if (Array.isArray(data.body) && data.body.length > 0) {
    return data.body as StoryBodyItem[];
  }
  return [];
}

export type CalendarMode = 'auto' | 'manual';

// Resolve the rendering mode for a calendar block.
//
// `source` is canonical when present and is only ever written by the
// editor. Legacy blocks that predate the events feature have no
// `source` key — they stay in 'manual' mode so the existing
// hand-entered events render exactly as they did before.
//
// Do NOT change this to fall back to 'auto' on absent `source`. That
// would silently swap rendering behavior on every legacy block on
// the first reload after deploy. See CLAUDE.md (April 2026 incident).
export function resolveCalendarMode(data: { source?: unknown }): CalendarMode {
  if (data.source === 'auto') return 'auto';
  if (data.source === 'manual') return 'manual';
  return 'manual';
}

// ============================================================
// Coverage headlines — what an editor would scan to avoid
// double-covering a story already in a recent draft. Pulled from
// `story.headline` (one per block) and `bulletin.items[].headline`
// (one per item). Other block types are intentionally skipped.
//
// Unedited placeholder strings from BLOCK_SCHEMAS defaults are
// filtered so a fresh-but-untouched draft doesn't pollute the list.
// The set is hardcoded because block-utils lives in /shared and
// can't import from the React-only blocks.tsx; if you change a
// default headline string in BLOCK_SCHEMAS, mirror it here.
// ============================================================

const PLACEHOLDER_HEADLINES = new Set<string>([
  'Headline goes here — make it specific and human',
  'Item one',
  'Item two',
  'Item three',
]);

function cleanHeadline(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (PLACEHOLDER_HEADLINES.has(trimmed)) return null;
  return trimmed;
}

export function extractCoverageHeadlines(
  blocks: Array<{ type: string; data: unknown }>,
): CoverageHeadline[] {
  const out: CoverageHeadline[] = [];
  for (const block of blocks) {
    if (!block || typeof block !== 'object') continue;
    const data = block.data as Record<string, unknown> | null | undefined;
    if (!data || typeof data !== 'object') continue;

    if (block.type === 'story') {
      const headline = cleanHeadline(data.headline);
      if (headline) out.push({ kind: 'story', headline });
      continue;
    }

    if (block.type === 'bulletin') {
      const items = Array.isArray(data.items) ? data.items : [];
      for (const item of items) {
        if (!item || typeof item !== 'object') continue;
        const headline = cleanHeadline((item as Record<string, unknown>).headline);
        if (headline) out.push({ kind: 'bulletin', headline });
      }
    }
  }
  return out;
}

// Adapter: turns a DB EventListItem into the in-block CalendarEventItem
// shape (null → undefined). The DB id is preserved as `_dbId` so
// renderers can correlate with hidden-id filters; manual events have
// no `_dbId`.
export interface CalendarRenderEvent extends CalendarEventItem {
  _dbId?: string;
}

export function dbEventToRenderEvent(e: EventListItem): CalendarRenderEvent {
  return {
    name: e.name,
    description: e.description ?? undefined,
    linkLabel: e.linkLabel ?? undefined,
    linkUrl: e.linkUrl ?? undefined,
    startDate: e.startDate,
    startTime: e.startTime ?? undefined,
    endDate: e.endDate ?? undefined,
    endTime: e.endTime ?? undefined,
    _dbId: e.id,
  };
}

// Compute the events a calendar block should display.
//
// 'auto' mode: take the current 8-day DB window, drop anything in
//   `hiddenEventIds`, append the user's ad-hoc `extraEvents`, sort.
// 'manual' mode: just sort the legacy `events` array. dbEvents is
//   ignored.
export function resolveCalendarEvents(
  data: {
    source?: unknown;
    events?: unknown;
    extraEvents?: unknown;
    hiddenEventIds?: unknown;
  },
  dbEvents: EventListItem[],
): CalendarRenderEvent[] {
  const mode = resolveCalendarMode(data);
  if (mode === 'manual') {
    const events = Array.isArray(data.events) ? (data.events as CalendarEventItem[]) : [];
    return sortEvents(events.map((e) => ({ ...e })));
  }
  const hidden = new Set(
    Array.isArray(data.hiddenEventIds) ? (data.hiddenEventIds as string[]) : [],
  );
  const pulled = dbEvents
    .filter((e) => !hidden.has(e.id))
    .map(dbEventToRenderEvent);
  const extras = Array.isArray(data.extraEvents)
    ? (data.extraEvents as CalendarEventItem[]).map((e) => ({ ...e }))
    : [];
  return sortEvents([...pulled, ...extras]);
}
